/**
 * Il banco: le cassette aperte e quelle già marcate pronte.
 */

import type { EsitoComando } from '@errebi/shared';
import type { BenchView } from '@errebi/domain';

import type { creaBanco } from './api';

/** Il banco: le cassette aperte e quelle già marcate pronte. */
export function Banco({
  v,
  fai,
  chiediSvuota,
}: {
  v: BenchView;
  fai: (a: (b: ReturnType<typeof creaBanco>) => Promise<EsitoComando>) => () => void;
  /** Svuotare non parte da qui: la tessera **chiede**, il dialogo decide. */
  chiediSvuota: (codice: string) => void;
}) {
  const confermate = v.cassetteBanco.filter((c) => c.confermata).map((c) => c.codice);

  return (
    <div className="banco">
      <div className="mods" style={{ marginBottom: 8 }}>
        <span className="gesto__k">Banco</span>
        <span className="mod__hint">
          {v.cassetteBanco.length} apert{v.cassetteBanco.length === 1 ? 'a' : 'e'}
          {/* Normalmente è zero, ed è il punto: quel numero conta le cassette che
              hanno lasciato il banco **senza** che il magazzino lo sappia. Se
              resta lì, la consegna non è passata — si riproverà da sé al gesto
              dopo. Prima contava tutto ciò che era mai passato di qui, cioè
              mentiva. */}
          {v.pronte.length > 0 && (
            <span className="mod__hint mod__hint--sveglia">
              {' '}
              · {v.pronte.length} non ancora {v.pronte.length === 1 ? 'passata' : 'passate'} al
              magazzino
            </span>
          )}
        </span>
        <button
          className="mod"
          disabled={confermate.length === 0}
          onClick={fai((b) => b.marcaPronte(confermate))}
        >
          Pronte per il magazzino
        </button>
      </div>
      <div className="cassette cassette--banco">
        {v.cassetteBanco.map((c) => (
          <div key={c.codice} className={`cassetta${c.aperta ? ' cassetta--aperta' : ''}`}>
            {/*
              Il nome è un link, e apre la stessa pagina del QR — in una scheda
              a parte, perché il banco non deve perdere il posto in cui era.

              Solo a cassetta confermata: quella pagina legge **un foglio**, e
              un foglio esiste solo dopo la conferma. Prima non c'è niente da
              aprire, e un link che porta a «questa cassetta non risulta»
              insegna a non fidarsi dei link.
            */}
            {c.confermata ? (
              <a
                className="cassetta__cod cassetta__cod--apre"
                href={`/cassetta/${c.codice}`}
                target="_blank"
                rel="noreferrer"
                title={`Apri il contenuto di ${c.codice}`}
              >
                {c.codice}
              </a>
            ) : (
              <span className="cassetta__cod">{c.codice}</span>
            )}
            <span className="cassetta__n">
              {c.capi}/{v.capienza} ·{' '}
              {c.confermata ? 'confermata' : `${c.daStampare.length} da etichettare`}
            </span>
            {/*
              La conferma sta qui e non nella barra della tastiera, ed è una
              scelta sulla regola «un gesto, un posto»: quella barra parla della
              cassetta **aperta**, cioè di quella che si sta contando, mentre
              confermare è ciò che si fa quando si smette. Qui la cassetta si
              vede accanto alle altre e accanto al gesto che viene dopo —
              «pronte per il magazzino» — che è l'ordine in cui le due cose
              succedono davvero.

              Vale per ogni cassetta al banco, non solo per l'aperta: a fine
              giro se ne confermano tre di fila senza riaprirle una a una.
            */}
            <span className="cassetta__gesti">
              <button
                className={'cassetta__ok' + (c.confermata ? ' cassetta__ok--fatto' : '')}
                disabled={c.capi === 0 || c.daStampare.length > 0}
                onClick={fai((b) => b.conferma(c.codice))}
              >
                {c.confermata ? '✓ Riconferma' : 'Conferma'}
              </button>
              {/*
                Svuotare sta accanto alla conferma perché è il suo contrario, e
                la stessa tessera è il posto dove si guarda la cassetta per
                decidere quale delle due cose fare. Non chiama niente: apre il
                dialogo, perché quel che disfa — capi da ricevere di nuovo,
                etichette già stampate che diventano carta da buttare — non si
                rifà.
              */}
              <button className="cassetta__via" onClick={() => chiediSvuota(c.codice)}>
                Svuota
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
